import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Copy, Users, Trophy, QrCode, Send, Loader2, Share2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { powerMyReferrals, powerRedeemReferral } from "@/lib/powerApi";
import PowerShareDialog from "@/components/power/PowerShareDialog";

export default function PowerReferrals() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [redeem, setRedeem] = useState("");
  const [busy, setBusy] = useState(false);
  const [share, setShare] = useState(false);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/power/referrals"); return; }
    powerMyReferrals().then(setData).catch(() => toast.error("Couldn't load referrals"));
  }, [loading, user, nav]);

  if (!data) return <div className="py-16 text-center text-[#0A1A2F]/50">Loading…</div>;

  const link = `${window.location.origin}/register?ref=${data.code}`;
  const copyLink = async () => { try { await navigator.clipboard.writeText(link); toast.success("Link copied"); } catch { toast.error("Couldn't copy"); } };
  const copyCode = async () => { try { await navigator.clipboard.writeText(data.code); toast.success("Code copied"); } catch { toast.error("Couldn't copy"); } };

  const doRedeem = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await powerRedeemReferral(redeem);
      toast.success(`Welcome bonus! ${r.referred_by_name ? "Referred by " + r.referred_by_name : ""}`);
      setRedeem("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't redeem");
    } finally { setBusy(false); }
  };

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=8&data=${encodeURIComponent(link)}`;

  return (
    <div data-testid="power-referrals" className="pb-6 space-y-5">
      <section className="rounded-3xl bg-gradient-to-br from-[#1E88FF] to-[#0A1A2F] text-white p-6 sm:p-8 shadow-xl relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 bg-white/15 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3">
            <Users className="w-3 h-3" /> Invite & earn
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Bring your crew.</h1>
          <p className="text-white/80 mt-1 text-sm max-w-md">Share your link — every friend who joins credits your Power wallet and unlocks Ambassador badges.</p>

          <div className="mt-5 bg-white/10 rounded-2xl p-4 backdrop-blur">
            <div className="text-[10px] font-bold uppercase tracking-widest text-white/70">Your code</div>
            <div className="flex items-center gap-2 mt-1">
              <div data-testid="ref-code" className="text-3xl font-black tracking-tighter flex-1">{data.code}</div>
              <button data-testid="ref-copy-code" onClick={copyCode} className="inline-flex items-center gap-1 bg-white text-[#0A1A2F] font-bold text-xs px-3 py-2 rounded-full"><Copy className="w-3 h-3" /> Copy</button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button data-testid="ref-copy-link" onClick={copyLink} className="inline-flex items-center gap-1 bg-white/15 text-white font-semibold text-xs px-3 py-2 rounded-full hover:bg-white/25"><Copy className="w-3 h-3" /> Copy link</button>
              <button data-testid="ref-share" onClick={() => setShare(true)} className="inline-flex items-center gap-1 bg-white/15 text-white font-semibold text-xs px-3 py-2 rounded-full hover:bg-white/25"><Share2 className="w-3 h-3" /> Share</button>
              <button data-testid="ref-qr" onClick={() => setShowQR(true)} className="inline-flex items-center gap-1 bg-white/15 text-white font-semibold text-xs px-3 py-2 rounded-full hover:bg-white/25"><QrCode className="w-3 h-3" /> QR</button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-[#E4EAF3] p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-lg bg-[#1E88FF]/10 text-[#1E88FF] flex items-center justify-center"><Send className="w-5 h-5" /></div>
          <div className="font-black text-[#0A1A2F]">Joined a friend? Redeem their code.</div>
        </div>
        <form onSubmit={doRedeem} className="flex flex-col sm:flex-row gap-2 mt-2">
          <input data-testid="ref-redeem-input" value={redeem} onChange={(e) => setRedeem(e.target.value.toUpperCase())} maxLength={16}
            placeholder="ENTER CODE" className="min-w-0 flex-1 px-4 py-3 rounded-xl bg-[#F6F8FC] border border-[#E4EAF3] focus:border-[#1E88FF] focus:outline-none font-black tracking-widest uppercase" />
          <button data-testid="ref-redeem-btn" type="submit" disabled={busy || !redeem.trim()} className="shrink-0 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#1E88FF] to-[#0A1A2F] text-white font-bold px-5 py-3 rounded-xl disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Redeem"}
          </button>
        </form>
      </section>

      <section className="bg-white rounded-2xl border border-[#E4EAF3] p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-lg bg-[#22C55E]/10 text-[#22C55E] flex items-center justify-center"><Trophy className="w-5 h-5" /></div>
          <div>
            <div className="font-black text-[#0A1A2F]">Friends who joined</div>
            <div className="text-xs text-[#0A1A2F]/60">{data.joined_count} so far</div>
          </div>
        </div>
        {data.joined.length === 0 ? (
          <div data-testid="ref-empty" className="text-center py-8 text-[#0A1A2F]/50 text-sm">No one yet — share your link to get started.</div>
        ) : (
          <ul className="divide-y divide-[#E4EAF3]">
            {data.joined.map((j) => (
              <li key={j.id} className="py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center font-black text-white" style={{ background: j.avatar_color }}>
                  {(j.name || "?")[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-[#0A1A2F] truncate">{j.name}</div>
                  <div className="text-[11px] text-[#0A1A2F]/50">{(j.created_at || "").slice(0, 10)}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {share && <PowerShareDialog url={link} title="Join me on Jixxeo Power" onClose={() => setShare(false)} />}
      {showQR && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowQR(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 shadow-2xl text-center">
            <img src={qrSrc} alt="QR" data-testid="ref-qr-image" className="w-64 h-64 rounded-xl border border-[#E4EAF3]" />
            <div className="text-xs text-[#0A1A2F]/60 mt-2">Scan to join with your code</div>
            <button onClick={() => setShowQR(false)} className="mt-3 text-sm text-[#1E88FF] font-semibold">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
